import { FreshnessNote } from "@/components/FreshnessNote";
import { freshnessDate } from "@/lib/currentYear";
import { useEffect, useState } from "react";
import { useNavigate } from "@/lib/router-compat";
import { supabase } from "@/integrations/supabase/client";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { ArticleCard } from "@/components/ArticleCard";
import { SectionRenderer } from "@/components/SectionRenderer";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Search } from "lucide-react";
import { SEOHelmet } from "@/components/SEOHelmet";
import { generateBreadcrumbSchema, generateCollectionPageSchema } from "@/lib/schemaGenerator";
import { articleListJsonLd } from "@/lib/schema-articles";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Link } from "@/lib/router-compat";

interface Article {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  featured_image_url: string | null;
  author: string | null;
  published_at: string | null;
  reading_time_minutes: number | null;
  category_id: string | null;
  exclude_from_listings?: boolean;
  article_categories?: {
    name: string;
  } | null;
}

interface Category {
  id: string;
  name: string;
  slug: string;
}

const ArticleListing = () => {
  const navigate = useNavigate();
  const { data: settings } = useSiteSettings();
  const [articles, setArticles] = useState<Article[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCategories();
    fetchArticles();
  }, []);

  useEffect(() => {
    fetchArticles();
  }, [selectedCategory, searchQuery]);

  useEffect(() => {
    if (selectedCategory !== "all" && !categories.some(c => c.slug === selectedCategory)) {
      setSelectedCategory("all");
    }
  }, [categories]);

  const fetchCategories = async () => {
    // First, get categories that have published articles
    const { data: usedCategoryRefs, error: usedErr } = await supabase
      .from("articles")
      .select("category_id")
      .eq("status", "published")
      .eq("exclude_from_listings", false)
      .eq("show_on_homepage", false)
      .not("category_id", "is", null);

    if (usedErr) {
      console.error("Error loading used categories:", usedErr);
      setCategories([]);
      return;
    }

    const categoryIds = (usedCategoryRefs || [])
      .map((r) => r.category_id)
      .filter(Boolean);

    if (categoryIds.length === 0) {
      setCategories([]);
      return;
    }

    // Fetch only categories that have published articles
    const { data: cats, error: catsErr } = await supabase
      .from("article_categories")
      .select("id, name, slug")
      .in("id", categoryIds)
      .order("name");

    if (catsErr) {
      console.error("Error loading categories:", catsErr);
      setCategories([]);
      return;
    }

    setCategories(cats || []);
  };

  const fetchArticles = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from("articles")
        .select(`
          id,
          slug,
          title,
          excerpt,
          featured_image_url,
          author,
          published_at,
          reading_time_minutes,
          category_id,
          article_categories (
            name
          )
        `)
        .eq("status", "published")
        .eq("show_on_homepage", false)
        .eq("exclude_from_listings", false)
        .order("published_at", { ascending: false });

      if (selectedCategory !== "all") {
        const category = categories.find((c) => c.slug === selectedCategory);
        if (category) {
          query = query.eq("category_id", category.id);
        }
      }

      if (searchQuery.trim()) {
        query = query.ilike("title", `%${searchQuery}%`);
      }

      const { data, error } = await query;

      if (error) throw error;
      setArticles(data || []);
    } catch (error) {
      console.error("Error fetching articles:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleNavigate = (page: string) => {
    if (page === 'home') {
      navigate('/');
    }
  };

  const handleCardClick = (slug: string) => {
    navigate(`/kreditkarten/${slug}`);
  };

  const handleViewAllCards = () => {
    navigate('/');
  };

  // Generate schemas (only if settings are loaded)
  const domain = settings?.domain || '';
  const breadcrumbSchema = settings ? generateBreadcrumbSchema([
    { name: settings.breadcrumb_translations?.home || 'Startseite', url: domain },
    { name: settings.breadcrumb_translations?.articles || 'Ratgeber', url: `${domain}/ratgeber` }
  ]) : null;
  
  const articleListSchema = settings && articles.length > 0 ? articleListJsonLd({
    pageUrl: `${domain}/ratgeber`,
    items: articles.map(a => ({
      url: `${domain}/ratgeber/${a.slug}`,
      headline: a.title,
      description: a.excerpt || undefined,
      image: a.featured_image_url || undefined,
      datePublished: a.published_at || undefined
    })),
    listName: "Ratgeber & Artikel",
    listDescription: "Entdecken Sie unsere Artikel über Kreditkarten, Finanzen und kluge Geldentscheidungen"
  }) : null;

  const collectionPageSchema = settings ? generateCollectionPageSchema(
    `${domain}/ratgeber`,
    "Ratgeber",
    "Entdecken Sie unsere Artikel über Kreditkarten, Finanzen und kluge Geldentscheidungen",
    articleListSchema,
    breadcrumbSchema
  ) : null;

  return (
    <div className="min-h-screen flex flex-col">
      <SEOHelmet
        title={settings?.seo_templates?.article_listing.title || "Ratgeber & Artikel zu Kreditkarten, Zinsen und Finanzen"}
        description={settings?.seo_templates?.article_listing.description || "Lesen Sie hilfreiche Ratgeber rund um Kreditkarten, Zinsen, Sicherheit und Finanzentscheidungen."}
        type="website"
        url={`${settings?.domain || ''}/ratgeber`}
        modifiedTime={freshnessDate()}
        structuredData={collectionPageSchema}
      />

      <Header onNavigate={handleNavigate} currentPage="article-listing" />

      {/* Breadcrumb Section */}
      <section className="bg-gradient-to-b from-gray-50 to-white py-4 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumb className="mb-0">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/">{settings?.breadcrumb_translations?.home || 'Startseite'}</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{settings?.breadcrumb_translations?.articles || 'Ratgeber'}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="mb-12 text-center">
          <h1 className="mb-4 text-4xl font-bold md:text-5xl">
            {settings?.seo_templates?.article_listing.h1 || "Alle Artikel & Ratgeber"}
          </h1>
          <p className="mx-auto max-w-2xl text-lg text-muted-foreground">
            Entdecken Sie unsere Artikel über Kreditkarten, Finanzen und kluge Geldentscheidungen.
          </p>
          <FreshnessNote className="mt-3" label="Zuletzt geprüft" />
        </div>

        {/* Search and Filters */}
        <div className="mb-8 space-y-6">
          {/* Search Bar */}
          <div className="relative mx-auto max-w-md">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Artikel oder Themen suchen …"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Category Tabs */}
          <Tabs value={selectedCategory} onValueChange={setSelectedCategory}>
            <TabsList className="w-full justify-start overflow-x-auto">
              <TabsTrigger value="all">Alle</TabsTrigger>
              {categories.map((category) => (
                <TabsTrigger key={category.id} value={category.slug}>
                  {category.name}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>

        {/* Articles Grid */}
        {loading ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="h-96 animate-pulse rounded-lg bg-muted" />
            ))}
          </div>
        ) : articles.length === 0 ? (
          <div className="py-16 text-center">
            <p className="text-lg text-muted-foreground">
              Keine Artikel gefunden. Versuchen Sie eine andere Suche oder Kategorie.
            </p>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {articles.map((article) => (
              <ArticleCard
                key={article.id}
                slug={article.slug}
                title={article.title}
                excerpt={article.excerpt || undefined}
                featuredImage={article.featured_image_url || undefined}
                categoryName={article.article_categories?.name}
                author={article.author || undefined}
                publishedAt={article.published_at || undefined}
                readingTime={article.reading_time_minutes || undefined}
              />
            ))}
          </div>
        )}
      </div>

      {/* Dynamic Sections */}
      <SectionRenderer
        currentPage="article-listing"
        onViewDetails={handleCardClick}
        onViewAllCards={handleViewAllCards}
      />

      <Footer onNavigate={handleNavigate} />
    </div>
  );
};

export default ArticleListing;
